import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import DashboardLayout from "@/pages/Dashboard/DashboardLayout";
import Overview from "@/pages/Dashboard/Overview";
import Students from "@/pages/Dashboard/Students";
import StudentProfile from "@/pages/Dashboard/StudentProfile";
import Guardians from "@/pages/Dashboard/Guardians";
import Programs from "@/pages/Dashboard/Programs";
import Staff from "@/pages/Dashboard/Staff";
import StaffProfile from "@/pages/Dashboard/StaffProfile";
import StaffAttendance from "@/pages/Dashboard/StaffAttendance";
import AttendanceTerminal from "@/pages/AttendanceTerminal";
import Attendance from "@/pages/Dashboard/Attendance";
import Promotion from "@/pages/Dashboard/Promotion";
import QRScan from "@/pages/Dashboard/QRScan";
import FinanceEntry from "@/pages/Dashboard/FinanceEntry";
import FeeStructures from "@/pages/Dashboard/FeeStructures";
import FinanceOverview from "@/pages/Dashboard/FinanceOverview";
import FinanceAccounts from "@/pages/Dashboard/FinanceAccounts";
import FinanceAccountDetail from "@/pages/Dashboard/FinanceAccountDetail";
import FinanceOperations from "@/pages/Dashboard/FinanceOperations";
import FinanceScholarships from "@/pages/Dashboard/FinanceScholarships";
import FinanceClassSummary from "@/pages/Dashboard/FinanceClassSummary";
import FinanceEstimates from "@/pages/Dashboard/FinanceEstimates";
import FinanceChargeCatalogue from "@/pages/Dashboard/FinanceChargeCatalogue";
import Exams from "@/pages/Dashboard/Exams";
import Subjects from "@/pages/Dashboard/Subjects";
import ScoreEntry from "@/pages/Dashboard/ScoreEntry";
import ReportCards from "@/pages/Dashboard/ReportCards";
import Broadsheet from "@/pages/Dashboard/Broadsheet";
import AssessmentConfig from "@/pages/Dashboard/AssessmentConfig";
import IDCards from "@/pages/Dashboard/IDCards";
import Inventory from "@/pages/Dashboard/Inventory";
import TermlyEntitlements from "@/pages/Dashboard/TermlyEntitlements";
import Assets from "@/pages/Dashboard/Assets";
import AssetProfile from "@/pages/Dashboard/AssetProfile";
import Visitors from "@/pages/Dashboard/Visitors";
import Complaints from "@/pages/Dashboard/Complaints";
import Notifications from "@/pages/Dashboard/Notifications";
import Rooms from "@/pages/Dashboard/Rooms";
import Maintenance from "@/pages/Dashboard/Maintenance";
import UsersPage from "@/pages/Dashboard/Users";
import Audit from "@/pages/Dashboard/Audit";
import Settings from "@/pages/Dashboard/Settings";
import Organisation from "@/pages/Dashboard/Organisation";
import VerifyIdentityCard from "@/pages/VerifyIdentityCard";
import Reports from "@/pages/Dashboard/Reports";
import ReportCreate from "@/pages/Dashboard/ReportCreate";
import ReportDetail from "@/pages/Dashboard/ReportDetail";
import ParentEngagements from "@/pages/Parent/ParentEngagements";

import MobileLayout from "@/pages/Mobile/MobileLayout";
import MobileScan from "@/pages/Mobile/MobileScan";
import MobileClass from "@/pages/Mobile/MobileClass";
import MobileSearch from "@/pages/Mobile/MobileSearch";
import MobileNotifications from "@/pages/Mobile/MobileNotifications";
import MobileProfile from "@/pages/Mobile/MobileProfile";

import ParentLayout from "@/pages/Parent/ParentLayout";
import ParentHome from "@/pages/Parent/ParentHome";
import ChildDashboard, {
  OverviewTab,
  AcademicTab,
  AttendanceTab,
  FeesTab,
  NotificationsTab,
  InfoTab,
  TimetableTab,
  HomeworkTab,
  BehaviourTab,
} from "@/pages/Parent/ChildDashboard";
import ChildInsights from "@/pages/Parent/ChildInsights";
import PaymentConfirmation from "@/pages/Parent/PaymentConfirmation";
import ParentAssistant from "@/pages/Parent/ParentAssistant";
import StaffAssistant from "@/pages/Dashboard/StaffAssistant";

const ParentGate = ({ children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bone">
        <div className="text-brand-muted font-display">Loading...</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  const roles = user?.roles || [user?.role];
  // If parent role but no admin/teacher access, they belong on /parent
  const hasAdminSide = roles.some((r) =>
    [
      "super_admin",
      "director",
      "teacher",
      "receptionist",
      "accountant",
      "attendance_officer",
    ].includes(r),
  );
  if (roles.includes("parent") && !hasAdminSide)
    return <Navigate to="/parent" replace />;
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bone">
        <div className="text-brand-muted font-display">Loading...</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/attendance-terminal" element={<AttendanceTerminal />} />
          <Route path="/verify/id/:token" element={<VerifyIdentityCard />} />
          <Route
            path="/app"
            element={
              <ParentGate>
                <DashboardLayout />
              </ParentGate>
            }
          >
            <Route index element={<Overview />} />
            <Route path="students" element={<Students />} />
            <Route path="students/:id" element={<StudentProfile />} />
            <Route path="guardians" element={<Guardians />} />
            <Route path="programs" element={<Programs />} />
            <Route path="staff" element={<Staff />} />
            <Route path="staff/:id" element={<StaffProfile />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="staff-attendance" element={<StaffAttendance />} />
            <Route path="promotion" element={<Promotion />} />
            <Route path="qr-scan" element={<QRScan />} />
            <Route path="fees" element={<FinanceEntry />} />
            <Route path="fees/overview" element={<FinanceOverview />} />
            <Route path="fees/accounts" element={<FinanceAccounts />} />
            <Route path="fees/accounts/:studentId" element={<FinanceAccountDetail />} />
            <Route path="fees/reconciliation" element={<FinanceAccounts reconciliationOnly />} />
            <Route path="fees/operations" element={<FinanceOperations />} />
            <Route path="fees/scholarships" element={<FinanceScholarships />} />
            <Route path="fees/structures" element={<FeeStructures />} />
            <Route path="fees/class-summary" element={<FinanceClassSummary />} />
            <Route path="fees/estimates" element={<FinanceEstimates />} />
            <Route path="fees/catalogue" element={<FinanceChargeCatalogue />} />
            <Route path="exams" element={<Exams />} />
            <Route path="subjects" element={<Subjects />} />
            <Route path="score-entry" element={<ScoreEntry />} />
            <Route path="report-cards" element={<ReportCards />} />
            <Route path="broadsheet" element={<Broadsheet />} />
            <Route path="assessment-config" element={<AssessmentConfig />} />
            <Route path="id-cards" element={<IDCards />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/entitlements" element={<TermlyEntitlements />} />
            <Route path="assets" element={<Assets />} />
            <Route path="assets/:id" element={<AssetProfile />} />
            <Route path="visitors" element={<Visitors />} />
            <Route path="complaints" element={<Complaints />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="rooms" element={<Rooms />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit" element={<Audit />} />
            <Route path="settings" element={<Settings />} />
            <Route path="organisation" element={<Organisation />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/new" element={<ReportCreate />} />
            <Route path="reports/:id" element={<ReportDetail />} />
            <Route path="assistant" element={<StaffAssistant />} />
          </Route>
          <Route
            path="/m"
            element={
              <ProtectedRoute>
                <MobileLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<MobileScan />} />
            <Route path="class" element={<MobileClass />} />
            <Route path="search" element={<MobileSearch />} />
            <Route path="notifications" element={<MobileNotifications />} />
            <Route path="profile" element={<MobileProfile />} />
          </Route>

          {/* PARENT PORTAL */}
          <Route
            path="/parent"
            element={
              <ProtectedRoute>
                <ParentLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ParentHome />} />
            <Route path="payment-confirmation" element={<PaymentConfirmation />} />
            <Route path="engagements" element={<ParentEngagements />} />
            <Route path="assistant" element={<ParentAssistant />} />
            <Route path="child/:id" element={<ChildDashboard />}>
              <Route index element={<OverviewTab />} />
              <Route path="overview" element={<OverviewTab />} />
              <Route path="academic" element={<AcademicTab />} />
              <Route path="attendance" element={<AttendanceTab />} />
              <Route path="timetable" element={<TimetableTab />} />
              <Route path="homework" element={<HomeworkTab />} />
              <Route path="fees" element={<FeesTab />} />
              <Route path="behaviour" element={<BehaviourTab />} />
              <Route path="notifications" element={<NotificationsTab />} />
              <Route path="info" element={<InfoTab />} />
              <Route path="insights" element={<ChildInsights />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
